'use client';
// Single shared subscriber for the smart-intake questionnaire context
// (localStorage['smartIntakeCtx']). Previously every WaSmartButton/OtherExperts/
// CategoryExpertsList instance ran its OWN 400-600ms setInterval — a category page
// with 30 cards burned 60+ localStorage reads + JSON.parse per second. Now ONE
// interval serves every subscriber, and parsing only happens when the raw string
// actually changed. (Why polling at all: the 'storage' event doesn't fire in the
// tab that wrote the value, and the questionnaire writes from this same tab.)

import { useSyncExternalStore } from 'react';
import { SMART_INTAKE_KEY } from '@/components/SmartIntakeBar';
import type { SmartIntakeContext } from '@/components/SmartIntakeBar';

type Listener = () => void;

let listeners = new Set<Listener>();
let timer: ReturnType<typeof setInterval> | null = null;
let lastRaw: string | null = null;
let cached: SmartIntakeContext | null = null;

function read() {
  let raw: string | null = null;
  try { raw = localStorage.getItem(SMART_INTAKE_KEY); } catch {}
  if (raw === lastRaw) return;
  lastRaw = raw;
  try { cached = raw ? JSON.parse(raw) : null; } catch { cached = null; }
  syncKnownCityCookie(cached);
  listeners.forEach(l => l());
}

// KNOWN-CITY COOKIE (Founder 30/09/26, "the page loads, refreshes, then loads OTHER experts"): the city lives in localStorage,
// which the server cannot read — so /<category> rendered its list around Tel Aviv and the browser re-ranked it around the
// visitor's remembered city right after hydration (a visible swap). Every read of the store mirrors ctx.city into ONE cookie
// that the server reads (lib/knownCityCookie.ts), so the first paint is already the final order. Every writer of the store
// ends in read() (or the 500ms poll), so this single door covers them all.
export const KNOWN_CITY_COOKIE = 'xc_city';
let lastCookie: string | null | undefined;
function syncKnownCityCookie(ctx: SmartIntakeContext | null): void {
  try {
    const city = ctx?.city ? String(ctx.city).slice(0, 60) : null;
    const v = city ? encodeURIComponent(JSON.stringify({ n: city, ...(typeof ctx!.cityLat === 'number' && typeof ctx!.cityLng === 'number' ? { lat: ctx!.cityLat, lng: ctx!.cityLng } : {}) })) : null;
    if (v === lastCookie) return;
    lastCookie = v;
    document.cookie = v ? `${KNOWN_CITY_COOKIE}=${v}; path=/; max-age=31536000; samesite=lax` : `${KNOWN_CITY_COOKIE}=; path=/; max-age=0; samesite=lax`;
  } catch {}
}
if (typeof window !== 'undefined') { try { read(); } catch {} } // mirror once at module load — even on pages with no subscriber

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  if (!timer) {
    read();
    timer = setInterval(read, 500);
  }
  return () => {
    listeners.delete(listener);
    if (!listeners.size && timer) { clearInterval(timer); timer = null; }
  };
}

const getSnapshot = () => cached;
const getServerSnapshot = () => null;

/** Live smart-intake context — null until the questionnaire writes something. */
export function useSmartIntakeCtx(): SmartIntakeContext | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// 2026-08-22 (user request): "בחירת עיר אחרת" reset chip on the category page — clears
// ONLY the city fields (keeps categorySlug/cleanNeed/answers etc. intact, unlike
// SmartIntakeBar's clear() which nukes the whole context) so every subscriber (this
// store's readers, e.g. CategoryExpertsList) and the questionnaire both go back to
// "no city known" and the questionnaire's city question returns instead of staying
// pre-answered/skipped. Calls `read()` synchronously right after writing instead of
// waiting for the 500ms poll, so the UI updates on the same click, not up to half a
// second later.
export function resetCity(): void {
  try {
    const raw = localStorage.getItem(SMART_INTAKE_KEY);
    const prev: SmartIntakeContext = raw ? JSON.parse(raw) : ({} as SmartIntakeContext);
    localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify({
      ...prev, city: null, cityLat: null, cityLng: null, cityAnsweredAt: null, cityChosenAt: null,
    }));
  } catch {}
  // Founder bug 2026-09-28 (/טכנאי-דודי-שמש: pick ירושלים → "עיר אחרת" → blank for a second → ירושלים again). Root cause:
  // the store was cleared but THIS PAGE's own pick (resolvePageCity rule 2, tab-local) survived; the questionnaire remounted,
  // asked resolvePageCity(), got ירושלים back from the page pick and wrote it into the store again. A reset clears every
  // source of "which city" for this page: store city + choice stamp + the page pick.
  _pagePick.delete(_path());
  lastRaw = null; // force read() to re-parse even though the interval last saw a different raw string
  read();
}

// 2026-09-27 (Founder: "המומחים ישתנו בזמן אמת"): the home smart search writes while the user types — publish() writes and
// notifies every subscriber on the same keystroke instead of waiting for the 500ms poll.
export function publishCtx(ctx: SmartIntakeContext | null): void {
  try { if (ctx) localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify(ctx)); else localStorage.removeItem(SMART_INTAKE_KEY); } catch {}
  lastRaw = null; read();
}

// DEMO channel — the idle search bar rotates examples ("טכנאי מזגנים בתל אביב"…) and the home experts follow them. Kept in
// memory only: an example is never persisted as the visitor's intent (the category pages read localStorage, not this).
// Also carries the visitor's live (unsubmitted) query on the home page — shown, never persisted.
// typed: true = the visitor's own live query (drives the home experts list); absent = an idle rotating example (the bar only —
// 30/09/26 the list no longer swaps with it: "loads, refreshes, loads OTHER experts").
export type SmartIntakeDemo = { categorySlug: string | null; categoryName: string | null; city: string | null; typed?: boolean } | null;
let demo: SmartIntakeDemo = null;
const demoListeners = new Set<Listener>();
export function setSmartIntakeDemo(d: SmartIntakeDemo): void {
  if (JSON.stringify(d) === JSON.stringify(demo)) return;
  demo = d; demoListeners.forEach(l => l());
}
export function useSmartIntakeDemo(): SmartIntakeDemo {
  return useSyncExternalStore(l => { demoListeners.add(l); return () => { demoListeners.delete(l); }; }, () => demo, () => null);
}

// 2026-09-28 (Founder: "שמירת העיר של הלקוח ברגע שיודעים לשימוש בשאר המקומות"): ONE door for "we now know the visitor's
// city" — the home smart search, the menu search, and any future surface call this; the questionnaire's city listener,
// the experts lists and the WhatsApp message all read the same stamped value in real time. Only the city fields change.
export function setKnownCity(name: string, coords?: { lat: number; lng: number } | null): void {
  try {
    const raw = localStorage.getItem(SMART_INTAKE_KEY);
    const prev: SmartIntakeContext = raw ? JSON.parse(raw) : ({ categorySlug: null, categoryName: null, city: null, cleanNeed: null, priceHint: null, timingHint: null } as SmartIntakeContext);
    if (prev.city === name && prev.cityChosenAt) return;
    localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify({ ...prev, city: name, cityLat: coords?.lat ?? null, cityLng: coords?.lng ?? null, cityAnsweredAt: Date.now(), cityChosenAt: Date.now() }));
  } catch {}
  lastRaw = null; read();
}

// Any writer of smartIntakeCtx calls this after writing, so every listener sees the new value on the SAME tick — the 500ms
// poll alone let a listener act on a stale city and write it back (race caught 2026-09-28).
export function notifySmartIntake(): void { lastRaw = null; read(); }
/** Synchronous read of the stored context — for effects that must not act on a stale subscribed copy. */
export function readSmartIntakeNow(): SmartIntakeContext | null {
  try { const raw = localStorage.getItem(SMART_INTAKE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

/** The visitor actively chose this city (questionnaire pick) — the only kind of write that overrides another open page's own city. */
export function markCityChosen(): void {
  try { const raw = localStorage.getItem(SMART_INTAKE_KEY); if (!raw) return; const c = JSON.parse(raw); c.cityChosenAt = Date.now(); localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify(c)); } catch {}
  lastRaw = null; read();
}

/** A /<category>/<city> page was opened: remember its city (page-load — stamps cityAnsweredAt, NOT cityChosenAt, so it never
 *  overrides a city the visitor actively chose in another open tab). Works even where the page has no questionnaire. */
export function rememberPageCity(name: string, coords?: { lat: number; lng: number } | null): void {
  try {
    const raw = localStorage.getItem(SMART_INTAKE_KEY);
    const prev: SmartIntakeContext = raw ? JSON.parse(raw) : ({ categorySlug: null, categoryName: null, city: null, cleanNeed: null, priceHint: null, timingHint: null } as SmartIntakeContext);
    if (prev.city === name && prev.cityAnsweredAt) return;
    if (prev.cityChosenAt && prev.city && isHistoryNavigation()) return; // Back/Forward never erases an active choice
    localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify({ ...prev, city: name, cityLat: coords?.lat ?? null, cityLng: coords?.lng ?? null, cityAnsweredAt: Date.now(), cityChosenAt: null }));
  } catch {}
  lastRaw = null; read();
}

// ═══ THE PAGE CITY — one resolver for every reader (bughunter round 3 BLOCKER 2026-09-28: badge, expert list and WhatsApp
// button each had their own rule → one screen showed נתניה / חיפה / אשקלון at once). Order:
//   1. an ACTIVE choice (cityChosenAt) made after this page opened or after the last pick here — any tab, any surface
//   2. the city the visitor picked ON THIS PAGE (tab-local)
//   3. this page's own URL city (registered by <RememberCity/> on /<category>/<city>)
//   4. the remembered city in the store
// Nothing on a page reads ctx.city directly for "which city" — it calls resolvePageCity()/usePageCity().
const _pageOpened = new Map<string, number>();
const _pagePick = new Map<string, { name: string; t: number }>();
const _pageUrlCity = new Map<string, string>();
const _path = () => (typeof location !== 'undefined' ? location.pathname : '');
export function pageOpenedAt(path = _path()): number {
  // a page reached by Back/Forward is not a new context: opened=0 → the visitor's saved choice stays the page city
  if (!_pageOpened.has(path)) _pageOpened.set(path, isHistoryNavigation() ? 0 : Date.now());
  return _pageOpened.get(path)!;
}
/** a page-load write of `name` is allowed only if it IS the page city (never over a newer / saved active choice) */
export function pageLoadMayWrite(name: string): boolean { return (resolvePageCity() ?? name) === name; }
/** called during render by <RememberCity/> — the page's own URL city */
export function registerPageUrlCity(name: string): void { const p = _path(); pageOpenedAt(p); _pageUrlCity.set(p, name); }
/** the visitor picked a city on this page (questionnaire city question) */
export function notePagePick(name: string): void { _pagePick.set(_path(), { name, t: Date.now() }); }
export function resolvePageCity(ctx: SmartIntakeContext | null = readSmartIntakeNow()): string | null {
  const p = _path(); const pick = _pagePick.get(p); const since = Math.max(pick?.t || 0, pageOpenedAt(p));
  if (ctx?.city && ctx.cityChosenAt && ctx.cityChosenAt >= since) return ctx.city;
  if (pick) return pick.name;
  const url = _pageUrlCity.get(p); if (url) return url;
  return ctx?.city || null;
}
export function usePageCity(): string | null { const ctx = useSmartIntakeCtx(); return resolvePageCity(ctx); }

// Back / Forward is not a choice: a page reached by history navigation must not overwrite a city the visitor actively chose
// (bughunter round 3: Back to /חשמלאי/חיפה erased the picked ירושלים for good).
let _popAt = 0;
if (typeof window !== 'undefined') window.addEventListener('popstate', () => { _popAt = Date.now(); });
export function isHistoryNavigation(): boolean {
  if (Date.now() - _popAt < 3000) return true;
  try { const n = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined; return n?.type === 'back_forward' && performance.now() < 5000; } catch { return false; }
}
